import React, { useEffect, useCallback, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { postsApi } from 'api';
import { v4 as UUIDv4 } from 'uuid';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { PostAttachment, PostAttachmentFileType, PostAttachmentLocal, PostStatus, UserData } from 'types/Dashboard';
import { useGlobal } from 'contexts/globalContext';
import { useAuth } from 'contexts/authContext';
import { DialogConfirm } from 'components/_new/Dialog';
import { Application } from 'types/Application';
import { readPostAttachmentTypeFromFile } from 'utils/postAttachment';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { ProgramPostForm, ProgramPostFormValues } from './ProgramPostForm';

export const ProgramPostEdit = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams();

	const { toastRef } = useGlobal();
	const { currentUser } = useAuth();
	const postId = Number(id);
	const { data: program } = useProgramPanel();

	const { control, handleSubmit, watch, reset, setValue } = useForm<ProgramPostFormValues>({
		defaultValues: {
			withEmail: true,
			pinned: false,
		},
	});

	const [attachments, setAttachments] = useState<PostAttachment[]>([]);

	const { data, refetch, isError, error } = useQuery(['getPostToEdit', id], () => postsApi.getPostAdmin({ postId }), {
		onSuccess: (data) => {
			if (data?.attachments) {
				setAttachments(data.attachments);
			}
			const applicationIds = (data.applications || []).map(({ id }) => id);
			const prepareRecipients = () => {
				if (!data.menteeAccess && !data.mentorAccess && applicationIds.length > 0) {
					return 'selected';
				}
				if (data.menteeAccess && data.mentorAccess) {
					return 'all';
				}
				if (data.menteeAccess && !data.mentorAccess) {
					return 'mentees';
				}
				if (data.mentorAccess && !data.menteeAccess) {
					return 'mentors';
				}
				return '';
			};
			reset({
				subject: data.title,
				content: data.text,
				recipients: prepareRecipients(),
				applications: applicationIds,
				withEmail: true,
				pinned: Boolean(data.pinnedAt),
			});
		},
		onError: () => {
			//
		},
	});

	useEffect(() => {
		refetch();
	}, []);

	const isEditingDraft = data?.status === PostStatus.DRAFT;

	const { mutate: editPostMutate, isLoading: editPostLoading } = useMutation(
		['editProgramPost'],
		postsApi.editPostAdmin,
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('programPanel.post.postHasBeenUpdated'),
				});
				navigate(`/program/${program?.name}/communication/list`);
			},
		}
	);

	const { mutate: editPostAsDraftMutate, isLoading: editPostAsDraftLoading } = useMutation(
		['editProgramPostAsDraft'],
		postsApi.editPostAdmin,
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('programPanel.post.draftHasBeenSaved'),
				});
				navigate(`/program/${program?.name}/communication/list`);
			},
		}
	);

	const handleFormSubmit = handleSubmit((values) => {
		const { withEmail, pinned, ...formData } = values;
		editPostMutate({
			postId,
			data: {
				...formData,
				status: PostStatus.PUBLISHED,
				withEmail: Boolean(withEmail),
				pinned: Boolean(pinned),
			},
		});
	});

	const handleSaveAsDraft = useCallback(() => {
		handleSubmit((values) => {
			const { ...formData } = values;
			editPostAsDraftMutate({
				postId,
				data: {
					...formData,
					status: PostStatus.DRAFT,
					withEmail: false,
					pinned: false,
				},
			});
		})();
	}, [editPostAsDraftMutate, handleSubmit, postId]);

	const subject = watch('subject');
	const content = watch('content');
	const recipients = watch('recipients');

	const hasSubject = subject && subject.length > 0;
	const hasContent = content && content.length > 0;

	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

	// upload post section
	const { mutateAsync: addPostAttachmentMutateAsync, isLoading: addPostAttachmentLoading } = useMutation(
		(data: FormData) => postsApi.addProgramPostAttachmentAdmin({ postId, data }),
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('programPanel.post.attachmentUploadSuccessfully'),
				});
			},
		}
	);

	const { mutateAsync: removePostAttachmentMutateAsync } = useMutation(postsApi.removeProgramPostAttachmentAdmin, {
		onSuccess: () => {
			toastRef?.current?.show({
				severity: 'success',
				life: 3000,
				summary: t('misc.success'),
				detail: t('programPanel.post.attachmentRemoveSuccessfully'),
			});
		},
	});

	const [newAttachments, setNewAttachments] = useState<PostAttachment[]>([]);
	const [uploadedAttachments, setUploadedAttachments] = useState<PostAttachmentLocal[]>([]);
	const handleAttachmentUpload = useCallback((file: File) => {
		const formData = new FormData();
		formData.append('file', file);

		const localUuid = UUIDv4();
		const { name: originalFileName, size: originalFileSize, type } = file;
		const fileType = readPostAttachmentTypeFromFile(type);

		const maxFileSize = 1024 * 1024 * 8;
		if (originalFileSize > maxFileSize) {
			toastRef?.current?.show({
				severity: 'error',
				life: 3000,
				summary: 'error',
				detail: t('misc.fileSizeExceeds', { count: 8 }),
			});
			return;
		}
		if (fileType === PostAttachmentFileType.UNKNOWN) {
			toastRef?.current?.show({
				severity: 'error',
				life: 3000,
				summary: 'error',
				detail: t('misc.fileInvalidFileType'),
			});
			return;
		}

		const postAttachmentLocal: PostAttachmentLocal = {
			postUuidLink: '',
			localUuid,
			originalFileName,
			originalFileSize,
			fileType,
			createdAt: new Date(),
		};
		setUploadedAttachments((prev) => [...prev, postAttachmentLocal]);
		addPostAttachmentMutateAsync(formData)
			.then((result) => {
				setUploadedAttachments((prev) => prev.filter((attachment) => attachment.localUuid !== localUuid));
				setNewAttachments((prev) => [...prev, result]);
				return result;
			})
			.catch(() => {
				toastRef?.current?.show({
					severity: 'error',
					life: 3000,
					summary: t('misc.error'),
					detail: t('misc.fileUnexpectedError'),
				});
			})
			.finally(() => {
				setTimeout(() => {
					setUploadedAttachments((prev) => prev.filter((attachment) => attachment.localUuid !== localUuid));
				}, 1000);
			});
	}, []);

	const handleAttachmentRemove = useCallback(
		(attachment: PostAttachment) =>
			removePostAttachmentMutateAsync(attachment.id)
				.then((result) => {
					if (result) {
						setAttachments((prev) => prev.filter(({ id }) => id !== attachment.id));
						setNewAttachments((prev) => prev.filter(({ id }) => id !== attachment.id));
					}
					return result;
				})
				.catch(() => {
					toastRef?.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.success'),
						detail: t('misc.fileUnexpectedError'),
					});
				}),
		[]
	);

	const isLoading = editPostLoading || editPostAsDraftLoading;

	return (
		<>
			<Helmet title={t('organizationPanel.programCommunicationEditPost')} />
			<SubPageTitle
				title={t('organizationPanel.programCommunicationEditPost')}
				additional={program?.displayName}
				right={
					<Link
						to={`/program/${program?.name}/communication/list`}
						className="p-button p-button-text p-button-sm p-0"
					>
						{t('programPanel.backToList')}
					</Link>
				}
			/>

			{isError ? (
				<ErrorContainer
					actionName={t('programPanel.backToList')}
					action={() => {
						navigate(`/program/${program?.name}/communication/list`);
					}}
					error={error}
				/>
			) : (
				<>
					<DialogConfirm
						visible={confirmDialogOpen}
						onHide={() => setConfirmDialogOpen(false)}
						title={t('programPanel.post.saveConfirm')}
						confirmAction={{ submit: true, form: 'edit-post-form' }}
					/>

					<ProgramPostForm
						formId="edit-post-form"
						onSubmit={handleFormSubmit}
						control={control}
						setValue={setValue}
						recipients={recipients || ''}
						defaultEditorContent={data?.text}
						attachments={attachments}
						newAttachments={newAttachments}
						uploadedAttachments={uploadedAttachments}
						onAttachmentUpload={handleAttachmentUpload}
						onAttachmentRemove={handleAttachmentRemove}
						isAddingAttachment={addPostAttachmentLoading}
						showWithEmailCheckbox={Boolean(isEditingDraft)}
						showSaveAsDraft={Boolean(isEditingDraft)}
						hasSubject={Boolean(hasSubject)}
						hasContent={Boolean(hasContent)}
						onSaveAsDraft={handleSaveAsDraft}
						onConfirmSubmit={() => setConfirmDialogOpen(true)}
						previewLoading={isLoading}
						saveDraftLoading={isLoading}
						submitLoading={isLoading}
						submitLabel={isEditingDraft ? t('actions.publish', 'Opublikuj') : t('actions.save')}
						previewUser={currentUser as UserData}
						previewAttachments={[...attachments, ...newAttachments]}
						loadMentorsWhenRecipientsNotSelected
					/>
				</>
			)}
		</>
	);
};
